import { TimelineZoomProvider } from "../remotion-timeline/context/timeline-zoom-provider";
import { VideoPreview } from "../video-preview";
import { ActionRow } from "../remotion-timeline/components/action-row";
import { TimelineSizeProvider } from "../remotion-timeline/context/timeline-size-provider";
import {
  TimelineContainer,
  Timeline,
} from "../remotion-timeline/components/timeline";
import {
  BottomWrapper,
  CollapseButton,
  ConfigurationContentWrapper,
  ConfigurationWrapper,
  ContentWrapper,
  MobileViewWrapper,
  PlayerWrapper,
  SwitchWrapper,
  TopConfigurationWrapper,
} from "./styled";
import ContentConfiguration from "./configuration/content";
import { PlayerRef } from "@remotion/player";
import MobileTopBar from "./top-bar";
import { useTimelineContext } from "../remotion-timeline/context/use-timeline-context";
import Tabs from "../../../../../library/tabs";
import { useEffect, useRef, useState } from "react";
import PositionConfiguration from "./configuration/position";
import RotationConfiguration from "./configuration/rotation";
import AlignmentConfiguration from "./configuration/alignment";
import SizingConfiguration from "./configuration/sizing";
import ColorConfiguration from "./configuration/color";
import { useSettings } from "../context";
import { getIcon } from "../../../../../utils/get-icon";
import FontSelectConfiguration from "./configuration/font-select";
import FontSizingConfiguration from "./configuration/font-sizing";
import BackgroundConfiguration from "./configuration/background";
import Switch from "../../../../../library/switch";
import FontDecorationConfiguration from "./configuration/font-decoration";
import SelectedBar from "./selected-bar";

interface Props {
  playerRef: React.RefObject<PlayerRef>;
  timelineContainerRef: React.RefObject<HTMLDivElement>;
  timelineContainerWidth: number | undefined;
}

const MainViewMobile = ({
  playerRef,
  timelineContainerRef,
  timelineContainerWidth,
}: Props) => {
  const { selectedItem, setSelectedItem } = useTimelineContext();
  const { applyToAll, setApplyToAllForCurrentItem, setBottomPanelHeight } =
    useSettings();
  const [shouldEdit, setShouldEdit] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("content");
  const bottomWrapperRef = useRef<HTMLDivElement>(null);
  const [calculatedWidth, setCalculatedWidth] = useState<number | undefined>(
    timelineContainerWidth
  );

  const SELECTIONS = [
    {
      id: 0,
      key: "content",
      title: "Endre tekst",
      ariaLabel: "Endre tekst",
      display: (
        <ConfigurationContentWrapper>
          <ContentConfiguration />
        </ConfigurationContentWrapper>
      ),
    },
    {
      id: 1,
      key: "positioning",
      title: "Posisjonering",
      ariaLabel: "Posisjonering",
      display: (
        <ConfigurationContentWrapper>
          <PositionConfiguration />
        </ConfigurationContentWrapper>
      ),
    },
    {
      id: 2,
      key: "rotation",
      title: "Rotering",
      ariaLabel: "Rotering",
      display: (
        <ConfigurationContentWrapper>
          <RotationConfiguration />
        </ConfigurationContentWrapper>
      ),
    },
    {
      id: 3,
      key: "alignment",
      title: "Tekstplassering",
      ariaLabel: "Tekstplassering",
      display: (
        <ConfigurationContentWrapper>
          <AlignmentConfiguration />
        </ConfigurationContentWrapper>
      ),
    },
    {
      id: 4,
      key: "size",
      title: "Dimensjoner",
      ariaLabel: "Dimensjoner",
      display: (
        <ConfigurationContentWrapper>
          <SizingConfiguration />
        </ConfigurationContentWrapper>
      ),
    },
    {
      id: 5,
      key: "font-select",
      title: "Skrifttype",
      ariaLabel: "Skrifttype",
      display: (
        <ConfigurationContentWrapper>
          <FontSelectConfiguration />
        </ConfigurationContentWrapper>
      ),
    },
    {
      id: 6,
      key: "font-size",
      title: "Skriftstørrelse",
      ariaLabel: "Skriftstørrelse",
      display: (
        <ConfigurationContentWrapper>
          <FontSizingConfiguration />
        </ConfigurationContentWrapper>
      ),
    },
    {
      id: 6,
      key: "font-decoration",
      title: "Skriftdekorasjon",
      ariaLabel: "Skriftdekorasjon",
      display: (
        <ConfigurationContentWrapper>
          <FontDecorationConfiguration />
        </ConfigurationContentWrapper>
      ),
    },
    {
      id: 6,
      key: "color",
      title: "Farge",
      ariaLabel: "Farge",
      display: (
        <ConfigurationContentWrapper>
          <ColorConfiguration />
        </ConfigurationContentWrapper>
      ),
    },
    {
      id: 7,
      key: "background",
      title: "Bakgrunn",
      ariaLabel: "Bakgrunn",
      display: (
        <ConfigurationContentWrapper>
          <BackgroundConfiguration />
        </ConfigurationContentWrapper>
      ),
    },
  ];

  const handleApplyToAllChange = () => {
    setApplyToAllForCurrentItem(!applyToAll);
  };

  const clickCollapse = () => {
    setSelectedItem(null);
    setShouldEdit(false);
  };

  useEffect(() => {
    if (!bottomWrapperRef.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.contentRect.height;
        setBottomPanelHeight(height);
      }
    });
    resizeObserver.observe(bottomWrapperRef.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, [setBottomPanelHeight]);

  useEffect(() => {
    if (!timelineContainerWidth && timelineContainerRef.current) {
      setCalculatedWidth(timelineContainerRef.current.offsetWidth);
    } else {
      setCalculatedWidth(timelineContainerWidth);
    }

    const timeoutId = setTimeout(() => {
      if (timelineContainerRef.current) {
        setCalculatedWidth(timelineContainerRef.current.offsetWidth);
      }

      window.dispatchEvent(new Event("resize"));
    }, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [timelineContainerRef, timelineContainerWidth]);

  useEffect(() => {
    if (timelineContainerWidth) {
      setCalculatedWidth(timelineContainerWidth);
    }
  }, [timelineContainerWidth]);

  return (
    <MobileViewWrapper>
      <TimelineZoomProvider initialZoom={1}>
        <MobileTopBar />
        <ContentWrapper>
          <PlayerWrapper>
            <VideoPreview loop playerRef={playerRef} />
          </PlayerWrapper>
        </ContentWrapper>
        <BottomWrapper ref={bottomWrapperRef}>
          {selectedItem && shouldEdit ? (
            <>
              <TopConfigurationWrapper>
                <SwitchWrapper>
                  <Switch
                    checked={applyToAll}
                    onChange={handleApplyToAllChange}
                    label="Bruk på alle"
                    size="small"
                  />
                </SwitchWrapper>
                <CollapseButton onClick={clickCollapse}>
                  {getIcon("arrow-down")}
                </CollapseButton>
              </TopConfigurationWrapper>
              <ConfigurationWrapper>
                <Tabs
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  selections={SELECTIONS}
                />
              </ConfigurationWrapper>
            </>
          ) : (
            <>
              {selectedItem ? (
                <SelectedBar
                  playerRef={playerRef}
                  setShouldEdit={setShouldEdit}
                />
              ) : (
                <ActionRow playerRef={playerRef} />
              )}
              <TimelineContainer timelineContainerRef={timelineContainerRef}>
                {calculatedWidth ? (
                  <TimelineSizeProvider containerWidth={calculatedWidth}>
                    <Timeline playerRef={playerRef} />
                  </TimelineSizeProvider>
                ) : (
                  <div style={{ height: "100%", width: "100%" }} />
                )}
              </TimelineContainer>
            </>
          )}
        </BottomWrapper>
      </TimelineZoomProvider>
    </MobileViewWrapper>
  );
};

export default MainViewMobile;
